import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useReviewStore, loadDiagramReview } from '../../store/useReviewStore';
import { useCollectionStore } from '../../store/useCollectionStore';
import { useSSEStore } from '../../store/useSSEStore';
import { useProjectStore } from '../../store/useProjectStore';
import { useExtractionStore } from '../../store/useExtractionStore';
import { useAuthStore } from '../../store/useAuthStore';
import * as api from '../../api/endpoints';
import { ReviewDescription } from './ReviewDescription';
import { PublishPreviewModal } from './PublishPreviewModal';
import type { ReviewBoqItem, LvMatch, BoqPosition, MatchSummary } from '../../types';

type DiagramStage = 'collected' | 'grouped' | 'matched';

interface PublishResult {
  location: string;
  artifacts: number;
  boq_items?: number;
  plans?: number;
}

/* ═══════════════════════════════════════════════════════════════
   Top-level ReviewTab — split panel + source tabs
   ═══════════════════════════════════════════════════════════════ */

export function ReviewTab() {
  return (
    <div className="review-container">
      <ReviewDescription />
      <div className="review-right-panel">
        <DiagramReview />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Diagram Review
   ═══════════════════════════════════════════════════════════════ */

function DiagramReview() {
  const isOwner = useAuthStore((s) => s.user?.role) === 'owner';
  const groupedItems = useReviewStore((s) => s.diagramGroupedItems);
  const matchedItems = useReviewStore((s) => s.diagramMatchedItems);
  const matchSummary = useReviewStore((s) => s.diagramMatchSummary);
  const groupedAt = useReviewStore((s) => s.diagramGroupedAt);
  const matchedAt = useReviewStore((s) => s.diagramMatchedAt);
  const groupingFlag = useReviewStore((s) => s.grouping);
  const matchingFlag = useReviewStore((s) => s.matching);
  const publishing = useReviewStore((s) => s.publishing);
  const expandedRows = useReviewStore((s) => s.expandedRows);
  const matchEditIndex = useReviewStore((s) => s.matchEditIndex);
  const showPublishPreview = useReviewStore((s) => s.showPublishPreview);
  const groupProgress = useSSEStore((s) => s.skillProgress['review_group']);
  const matchProgress = useSSEStore((s) => s.skillProgress['review_match']);
  const publishProgress = useSSEStore((s) => s.skillProgress['review_publish']);
  const diagramModifiedAt = useCollectionStore((s) => s.diagramModifiedAt);
  const uploadDbProgress = useSSEStore((s) => s.skillProgress['diagram_upload_db']);
  const [publishResult, setPublishResult] = useState<PublishResult | null>(null);
  const [viewStage, setViewStage] = useState<DiagramStage | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadingDbLocal, setUploadingDbLocal] = useState(false);

  // Auto-run emits these SSE keys without setting the manual flags, so treat
  // live progress as "running" too — disables the buttons and blocks a
  // concurrent manual upload during the automatic post-analysis one.
  const grouping = groupingFlag || !!(groupProgress && groupProgress.total > 0 && groupProgress.processed < groupProgress.total);
  const matching = matchingFlag || !!(matchProgress && matchProgress.total > 0 && matchProgress.processed < matchProgress.total);
  const uploadingDb = uploadingDbLocal || !!(uploadDbProgress && uploadDbProgress.total > 0 && uploadDbProgress.processed < uploadDbProgress.total);

  const dataStage: DiagramStage = matchedItems ? 'matched' : groupedItems ? 'grouped' : 'collected';
  const stageIdx = (s: DiagramStage) => ['collected', 'grouped', 'matched'].indexOf(s);
  const stage: DiagramStage = viewStage && stageIdx(viewStage) <= stageIdx(dataStage) ? viewStage : dataStage;

  const lastProcessedAt = stage === 'matched' ? matchedAt : groupedAt;
  const staleFiles = useMemo(() => {
    if (!lastProcessedAt || stage === 'collected') return [];
    return Object.entries(diagramModifiedAt)
      .filter(([, ts]) => ts > lastProcessedAt)
      .map(([file]) => file.replace(/\.pdf$/i, ''));
  }, [lastProcessedAt, diagramModifiedAt, stage]);
  const isStale = staleFiles.length > 0;

  useEffect(() => {
    setLoading(true);
    loadDiagramReview().finally(() => setLoading(false));
  }, []);

  const handleGroup = async () => {
    useReviewStore.getState().setGrouping(true);
    useSSEStore.getState().clearSkillProgress('review_group');
    try {
      const result = await api.groupDiagrams() as any;
      if (result?.boq_items) {
        useReviewStore.getState().setDiagramGroupedItems(result.boq_items);
        useProjectStore.getState().setToast('Grouped & deduplicated', 'success');
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Group failed: ${e}`, 'error');
    }
    useReviewStore.getState().setGrouping(false);
  };

  const handleMatch = async () => {
    useReviewStore.getState().setMatching(true);
    useSSEStore.getState().clearSkillProgress('review_match');
    try {
      const result = await api.matchDiagrams() as any;
      if (result?.boq_items) {
        useReviewStore.getState().setDiagramMatchedItems(result.boq_items, result.match_summary);
        useProjectStore.getState().setToast('LV matching complete', 'success');
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Match failed: ${e}`, 'error');
    }
    useReviewStore.getState().setMatching(false);
  };

  const handlePublish = async () => {
    useReviewStore.getState().setShowPublishPreview(false);
    useReviewStore.getState().setPublishing(true);
    useSSEStore.getState().clearSkillProgress('review_publish');
    try {
      const result = await api.publishReview() as any;
      if (result) {
        useExtractionStore.getState().markPublished('__diagrams__', result.location ?? '');
        setPublishResult({ location: result.location, artifacts: result.artifacts, boq_items: result.boq_items });
      } else {
        useProjectStore.getState().setToast('Publish failed', 'error');
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Publish failed: ${e}`, 'error');
    }
    useReviewStore.getState().setPublishing(false);
  };

  const handleUploadToDb = async () => {
    setUploadingDbLocal(true);
    useSSEStore.getState().clearSkillProgress('diagram_upload_db');
    try {
      const { ok, data, error } = await api.uploadDiagramsToDb();
      if (ok && data && data.distribution_board_file_id) {
        const replaced = data.deleted_previous_id ? ' (replaced previous)' : '';
        useProjectStore.getState().setToast(
          `Uploaded ${data.diagrams} diagram(s), ${data.pages} page(s) to DB — board #${data.distribution_board_file_id}${replaced}`,
          'success');
      } else {
        // Surfaces the server error, incl. the 409 "upload already in progress"
        // when an automatic post-analysis upload is running for this project.
        useProjectStore.getState().setToast(`Upload to DB failed: ${error || 'unknown error'}`, 'error');
      }
    } catch (e) {
      useProjectStore.getState().setToast(`Upload to DB failed: ${e}`, 'error');
    }
    setUploadingDbLocal(false);
  };

  const items = stage === 'matched' ? matchedItems : stage === 'grouped' ? groupedItems : null;
  const summary = stage === 'matched' ? matchSummary : null;

  const groupHasProgress = grouping && groupProgress && groupProgress.total > 0;
  const matchHasProgress = matching && matchProgress && matchProgress.total > 0;
  const publishHasProgress = publishing && publishProgress && publishProgress.total > 0;

  return (
    <>
      {/* Wizard pipeline */}
      <WizardBar
        stage={stage}
        dataStage={dataStage}
        isOwner={isOwner}
        grouping={grouping}
        matching={matching}
        publishing={publishing}
        groupProgress={groupHasProgress ? groupProgress : null}
        matchProgress={matchHasProgress ? matchProgress : null}
        publishProgress={publishHasProgress ? publishProgress : null}
        onGroup={() => { handleGroup(); setViewStage(null); }}
        onMatch={() => { handleMatch(); setViewStage(null); }}
        onPublish={() => useReviewStore.getState().setShowPublishPreview(true)}
        onStageClick={(s) => setViewStage(s === dataStage ? null : s)}
      />

      {/* Upload diagrams to Monco DB (distribution-board file). Renders +
          uploads all reviewed diagrams, replacing the previous upload. */}
      {isOwner && items && items.length > 0 && (
        <div className="review-uploaddb-row">
          <button
            className="btn-review-action btn-publish"
            onClick={handleUploadToDb}
            disabled={uploadingDb || grouping || matching || publishing}
            title="Render & import all reviewed diagrams into Monco as one distribution-board file (replaces the previous upload)"
          >
            {uploadingDb
              ? (uploadDbProgress && uploadDbProgress.total > 0
                  ? `Uploading ${uploadDbProgress.processed}/${uploadDbProgress.total}…`
                  : 'Uploading to DB…')
              : 'Upload to DB'}
          </button>
          {uploadingDb && uploadDbProgress?.detail && (
            <span className="review-progress-detail">{uploadDbProgress.detail}</span>
          )}
        </div>
      )}

      {/* Stats bar */}
      {summary && (
        <div className="review-stats-bar">
          <MatchStats summary={summary} />
        </div>
      )}

      {/* Progress detail */}
      {groupHasProgress && groupProgress!.detail && <div className="review-progress-detail">{groupProgress!.detail}</div>}
      {matchHasProgress && matchProgress!.detail && <div className="review-progress-detail">{matchProgress!.detail}</div>}
      {publishHasProgress && publishProgress!.detail && <div className="review-progress-detail">{publishProgress!.detail}</div>}

      {/* Stale banner */}
      {isOwner && isStale && (
        <div className="review-stale-banner">
          Components changed in {staleFiles.join(', ')} since last {stage === 'matched' ? 'match' : 'grouping'} &mdash;{' '}
          <button className="review-stale-link" onClick={handleGroup} disabled={grouping}>
            {grouping ? 'Grouping...' : 'Re-group'}
          </button>{' '}to include updates.
        </div>
      )}

      {/* Loading */}
      {loading && <div className="review-loading">Loading review data...</div>}

      {/* Content */}
      {!loading && stage === 'collected' && !grouping && <CollectedView />}

      {stage !== 'collected' && items && items.length > 0 && (
        <CategoryGroupedTable
          items={items}
          showMatch={stage === 'matched'}
          expandedRows={expandedRows}
          matchEditIndex={matchEditIndex}
          type="diagram"
        />
      )}

      {summary && summary.unmatchedLvPositions.length > 0 && (
        <UnmatchedPanel positions={summary.unmatchedLvPositions} />
      )}

      {showPublishPreview && items && (
        <PublishPreviewModal
          items={items}
          summary={summary}
          type="diagrams"
          onConfirm={handlePublish}
          onCancel={() => useReviewStore.getState().setShowPublishPreview(false)}
        />
      )}
      {publishResult && <PublishResultModal result={publishResult} onClose={() => setPublishResult(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Wizard Action Bar
   ═══════════════════════════════════════════════════════════════ */

function WizardBar({ stage, dataStage, isOwner, grouping, matching, publishing, groupProgress, matchProgress, publishProgress, onGroup, onMatch, onPublish, onStageClick }: {
  stage: DiagramStage;
  dataStage: DiagramStage;
  isOwner: boolean;
  grouping: boolean;
  matching: boolean;
  publishing: boolean;
  groupProgress: any;
  matchProgress: any;
  publishProgress: any;
  onGroup: () => void;
  onMatch: () => void;
  onPublish: () => void;
  onStageClick: (s: DiagramStage) => void;
}) {
  const stages: { id: DiagramStage; label: string; number: number }[] = [
    { id: 'collected', label: 'Collected', number: 1 },
    { id: 'grouped', label: 'Grouped', number: 2 },
    { id: 'matched', label: 'Matched', number: 3 },
  ];
  const stageOrder = ['collected', 'grouped', 'matched'] as const;
  const currentIdx = stageOrder.indexOf(stage);
  const dataIdx = stageOrder.indexOf(dataStage);

  return (
    <div className="review-wizard-bar">
      <div className="review-wizard-steps">
        {stages.map((s, i) => {
          const completed = i < dataIdx;
          const active = i === currentIdx;
          const clickable = i <= dataIdx && i !== currentIdx;
          return (
            <div key={s.id} className="review-wizard-step-wrap">
              <div
                className={`review-wizard-step ${completed ? 'completed' : ''} ${active ? 'active' : ''} ${clickable ? 'clickable' : ''}`}
                onClick={clickable ? () => onStageClick(s.id) : undefined}
              >
                <span className="review-wizard-number">
                  {completed ? '\u2713' : s.number}
                </span>
                <span className="review-wizard-label">{s.label}</span>

                {/* Redo button on completed steps */}
                {isOwner && completed && s.id === 'grouped' && (
                  <button className="review-wizard-redo" onClick={(e) => { e.stopPropagation(); onGroup(); }} disabled={grouping} title="Re-group">
                    &#8635;
                  </button>
                )}
                {isOwner && completed && s.id === 'matched' && (
                  <button className="review-wizard-redo" onClick={(e) => { e.stopPropagation(); onMatch(); }} disabled={matching} title="Re-match">
                    &#8635;
                  </button>
                )}
              </div>

              {/* Primary action inline for current step */}
              {isOwner && active && s.id === 'collected' && (
                <button className="review-wizard-action" onClick={onGroup} disabled={grouping}>
                  {grouping
                    ? groupProgress ? `${groupProgress.processed}/${groupProgress.total}` : 'Grouping...'
                    : dataIdx > 0 ? 'Re-group' : 'Group & Deduplicate'}
                </button>
              )}
              {isOwner && active && s.id === 'grouped' && (
                <button className="review-wizard-action" onClick={onMatch} disabled={matching}>
                  {matching
                    ? matchProgress ? `${matchProgress.processed}/${matchProgress.total}` : 'Matching...'
                    : 'Match with LV'}
                </button>
              )}

              {i < stages.length - 1 && <span className="review-wizard-arrow">&rarr;</span>}
            </div>
          );
        })}
      </div>

      {/* Publish — separate row, only at matched stage */}
      {isOwner && dataStage === 'matched' && stage === 'matched' && (
        <button className="review-wizard-publish" onClick={onPublish} disabled={publishing}>
          {publishing
            ? publishProgress ? `Publishing ${publishProgress.processed}/${publishProgress.total}` : 'Publishing...'
            : 'Publish Results'}
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Category-Grouped Table
   ═══════════════════════════════════════════════════════════════ */

function CategoryGroupedTable({ items, showMatch, expandedRows, matchEditIndex, type }: {
  items: ReviewBoqItem[];
  showMatch: boolean;
  expandedRows: Set<number>;
  matchEditIndex: number | null;
  type: 'diagram' | 'plan';
}) {
  const expandedCategories = useReviewStore((s) => s.expandedCategories);
  const toggleCategory = useReviewStore((s) => s.toggleCategory);
  const setSelectedSourcePage = useReviewStore((s) => s.setSelectedSourcePage);

  const grouped = useMemo(() => {
    const map = new Map<string, Array<{ item: ReviewBoqItem; originalIndex: number }>>();
    items.forEach((item, idx) => {
      const key = item.item || 'Uncategorized';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({ item, originalIndex: idx });
    });
    // Sort groups: most items first
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [items]);

  const handleRowClick = useCallback((item: ReviewBoqItem) => {
    if (type === 'diagram' && item.diagrams.length > 0) {
      const d = item.diagrams[0];
      if (d.pages.length > 0) {
        const pageStr = d.pages[0].page; // e.g. "page_3.png"
        const pageNum = parseInt(pageStr.replace(/\D/g, ''), 10) || 1;
        const file = d.diagram.includes('.pdf') ? d.diagram : d.diagram + '.pdf';
        setSelectedSourcePage({ file, page: pageNum, type: 'diagram' });
      }
    }
  }, [type, setSelectedSourcePage]);

  return (
    <div className="review-table-wrap">
      {grouped.map(([category, entries]) => {
        const isExpanded = !expandedCategories.has(category); // default expanded
        const matchedCount = entries.filter((e) => e.item.lvMatch).length;
        const totalCount = entries.length;
        const highCount = entries.filter((e) => e.item.lvMatch?.confidence === 'high').length;
        const medCount = entries.filter((e) => e.item.lvMatch?.confidence === 'medium').length;
        const lowCount = entries.filter((e) => e.item.lvMatch?.confidence === 'low').length;

        // Accent color based on worst confidence
        const accentClass = !showMatch ? '' :
          matchedCount < totalCount || lowCount > 0 ? 'category-accent-low' :
          medCount > 0 ? 'category-accent-medium' : 'category-accent-high';

        return (
          <div key={category} className={`review-category-group ${accentClass}`}>
            <div className="review-category-header" onClick={() => toggleCategory(category)}>
              <span className={`zone-arrow ${isExpanded ? 'open' : ''}`}>&#9656;</span>
              <span className="review-category-name">{category}</span>
              <span className="review-category-count">{totalCount} item{totalCount !== 1 ? 's' : ''}</span>
              {showMatch && (
                <span className="review-category-conf">
                  {highCount > 0 && <span className="review-confidence-high">{highCount}H</span>}
                  {medCount > 0 && <span className="review-confidence-medium">{medCount}M</span>}
                  {lowCount > 0 && <span className="review-confidence-low">{lowCount}L</span>}
                  {totalCount - matchedCount > 0 && <span className="dim">{totalCount - matchedCount}?</span>}
                </span>
              )}
            </div>
            {isExpanded && (
              <table className="data-table review-table">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}></th>
                    <th>Spec</th>
                    <th className="qty">Qty</th>
                    <th>Source</th>
                    {showMatch && <th>LV Match</th>}
                    {showMatch && <th>Conf</th>}
                  </tr>
                </thead>
                <tbody>
                  {entries.map(({ item, originalIndex }) => (
                    <ReviewRow
                      key={originalIndex}
                      item={item}
                      index={originalIndex}
                      expanded={expandedRows.has(originalIndex)}
                      editing={matchEditIndex === originalIndex}
                      showMatch={showMatch}
                      onRowClick={() => handleRowClick(item)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Collected View
   ═══════════════════════════════════════════════════════════════ */

function CollectedView() {
  const diagrams = useCollectionStore((s) => s.diagrams);
  const uncollectDiagram = useCollectionStore((s) => s.uncollectDiagram);
  const files = Object.keys(diagrams);

  if (files.length === 0) {
    return (
      <div className="review-empty">
        <p>No diagrams collected yet.</p>
        <p className="dim">Go to the Extraction tab and click "Collect" on completed files.</p>
      </div>
    );
  }

  return (
    <div className="review-collected-view">
      <div className="review-collected-header">
        {files.length} file{files.length !== 1 ? 's' : ''} collected
      </div>
      <div className="review-collected-list">
        {files.map((f) => (
          <div key={f} className="review-collected-card">
            <span className="review-collected-file">{f.replace(/\.pdf$/i, '')}</span>
            <span className="review-collected-time dim">{new Date(diagrams[f].collectedAt).toLocaleTimeString()}</span>
            <button className="review-collected-remove" onClick={() => uncollectDiagram(f)} title="Remove from collection">&times;</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Shared Row Components
   ═══════════════════════════════════════════════════════════════ */

function ReviewRow({ item, index, expanded, editing, showMatch = true, onRowClick }: {
  item: ReviewBoqItem;
  index: number;
  expanded: boolean;
  editing: boolean;
  showMatch?: boolean;
  onRowClick?: () => void;
}) {
  const toggleRow = useReviewStore((s) => s.toggleRow);
  const setMatchEditIndex = useReviewStore((s) => s.setMatchEditIndex);
  const selectedSourcePage = useReviewStore((s) => s.selectedSourcePage);
  const match = item.lvMatch;
  const source = item.text === 'floor_plan' || item.diagrams.some(d => d.diagram.startsWith('fp_')) ? 'FP' : 'SLD';

  const confClass = match ? `review-row-conf-${match.confidence}` : 'review-row-conf-none';
  const qtyFlag = match?.quantityFlag;
  const qtyFlagClass = qtyFlag ? qtyFlag.severity === 'critical' ? 'review-qty-critical' : 'review-qty-warning' : '';
  const qtyTitle = qtyFlag
    ? `Qty mismatch (${qtyFlag.severity}): takeoff ${qtyFlag.boqQuantity} vs LV ${qtyFlag.lvQuantity} (${qtyFlag.delta > 0 ? '+' : ''}${qtyFlag.delta})`
    : undefined;

  // Check if this row's source page is currently selected
  const isSelected = (() => {
    if (!selectedSourcePage || !onRowClick) return false;
    if (item.diagrams.length > 0) {
      const d = item.diagrams[0];
      if (d.pages.length > 0) {
        const pageNum = parseInt(d.pages[0].page.replace(/\D/g, ''), 10) || 1;
        return selectedSourcePage.page === pageNum;
      }
    }
    return false;
  })();

  return (
    <>
      <tr
        className={`${confClass} ${expanded ? 'review-row-expanded' : ''} ${isSelected ? 'review-row-selected' : ''}`}
        onClick={onRowClick}
        style={onRowClick ? { cursor: 'pointer' } : undefined}
      >
        <td className="review-expand-cell" onClick={(e) => { e.stopPropagation(); toggleRow(index); }}>
          <span className={`zone-arrow ${expanded ? 'open' : ''}`}>&#9656;</span>
        </td>
        <td className="review-spec-cell" title={item.text}>{item.text || '-'}</td>
        <td className={`qty ${qtyFlagClass}`} title={qtyTitle}>{item.quantity}{item.unit === 'm' ? ' m' : ''}</td>
        <td><span className={`review-source-badge source-${source.toLowerCase()}`}>{source}</span></td>
        {showMatch && (
          <td className="review-match-cell" onClick={(e) => { e.stopPropagation(); setMatchEditIndex(editing ? null : index); }}>
            {editing ? (
              <MatchDropdown item={item} index={index} />
            ) : match ? (
              <span title={match.reasoning}>{match.lvId}</span>
            ) : (
              <span className="review-match-assign">Assign LV</span>
            )}
          </td>
        )}
        {showMatch && (
          <td>{match && <ConfidenceBadge confidence={match.confidence} />}</td>
        )}
      </tr>
      {expanded && (
        <tr className="review-detail-row">
          <td colSpan={showMatch ? 6 : 4}><RowDetail item={item} /></td>
        </tr>
      )}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Small Shared Components
   ═══════════════════════════════════════════════════════════════ */

function MatchStats({ summary }: { summary: MatchSummary }) {
  return (
    <div className="review-match-stats-row">
      <span className="review-stat-badge" title="Takeoff items matched to LV positions">
        {summary.matched}/{summary.totalBoqItems} matched
      </span>
      {summary.matchedHigh > 0 && <span className="review-stat-badge review-confidence-high" title="High confidence">{summary.matchedHigh}H</span>}
      {summary.matchedMedium > 0 && <span className="review-stat-badge review-confidence-medium" title="Medium confidence">{summary.matchedMedium}M</span>}
      {summary.matchedLow > 0 && <span className="review-stat-badge review-confidence-low" title="Low confidence">{summary.matchedLow}L</span>}
      {summary.quantityFlags > 0 && <span className="review-stat-badge review-qty-flag" title="Quantity mismatches">{summary.quantityFlags} qty flags</span>}
      {(summary.unmatchedLvPositions?.length ?? 0) > 0 && (
        <span className="review-stat-badge dim" title="LV positions with no matching takeoff item">
          {summary.unmatchedLvPositions.length} unmatched LV
        </span>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const labels: Record<string, string> = { high: 'HIGH', medium: 'MED', low: 'LOW', manual: 'MANUAL' };
  const tooltips: Record<string, string> = {
    high: 'High confidence', medium: 'Medium confidence — review recommended',
    low: 'Low confidence — likely needs correction', manual: 'Manually assigned',
  };
  return (
    <span className={`review-confidence-badge review-confidence-${confidence}`} title={tooltips[confidence] || ''}>
      {labels[confidence] || confidence.toUpperCase()}
    </span>
  );
}

function RowDetail({ item }: { item: ReviewBoqItem }) {
  return (
    <div className="review-row-detail">
      {item.diagrams.map((d) => (
        <div key={d.diagram} className="review-diagram-detail">
          <span className="review-diagram-name">{d.diagram}: {d.count}</span>
          <span className="review-diagram-pages">
            {d.pages.map((p) => `${p.page.replace('page_', 'p.').replace('.png', '')}: ${p.count}`).join(', ')}
          </span>
        </div>
      ))}
      {item.lvMatch?.reasoning && <div className="review-match-reasoning dim">{item.lvMatch.reasoning}</div>}
      {item.lvMatch?.quantityFlag && (
        <div className={`review-qty-detail ${item.lvMatch.quantityFlag.severity}`}>
          BoQ: {item.lvMatch.quantityFlag.boqQuantity} vs LV: {item.lvMatch.quantityFlag.lvQuantity}
          {' '}(delta: {item.lvMatch.quantityFlag.delta > 0 ? '+' : ''}{item.lvMatch.quantityFlag.delta})
        </div>
      )}
    </div>
  );
}

function UnmatchedPanel({ positions }: {
  positions: Array<{ id: string; name: string; section: string; quantity: number; unit: string }>;
}) {
  const [collapsed, setCollapsed] = useState(true);
  return (
    <div className="review-unmatched">
      <div className="review-unmatched-header" onClick={() => setCollapsed(!collapsed)}>
        <span className={`zone-arrow ${collapsed ? '' : 'open'}`}>&#9656;</span>
        Unmatched LV Positions ({positions.length})
      </div>
      {!collapsed && (
        <table className="data-table">
          <thead>
            <tr><th>Position</th><th>Description</th><th>Section</th><th className="qty">Qty</th><th>Unit</th></tr>
          </thead>
          <tbody>
            {positions.map((p) => (
              <tr key={p.id}>
                <td className="mono">{p.id}</td><td>{p.name}</td><td className="dim">{p.section}</td><td className="qty">{p.quantity}</td><td>{p.unit}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Match Dropdowns
   ═══════════════════════════════════════════════════════════════ */

function MatchDropdown({ item, index }: { item: ReviewBoqItem; index: number }) {
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(false);
  const boqExtraction = useProjectStore((s) => s.boqExtraction);
  const updateItemMatch = useReviewStore((s) => s.updateItemMatch);
  const setMatchEditIndex = useReviewStore((s) => s.setMatchEditIndex);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const positions = boqExtraction?.positions ?? [];

  const filtered = useMemo(() => {
    if (!search) return positions.slice(0, 50);
    const lower = search.toLowerCase();
    return positions.filter((p) => `${p.position_number || ''} ${p.description || ''}`.toLowerCase().includes(lower)).slice(0, 50);
  }, [positions, search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setMatchEditIndex(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [setMatchEditIndex]);

  const handleSelect = async (pos: BoqPosition | null) => {
    const lvMatch: LvMatch | null = pos ? {
      lvId: pos.position_number || '', lvName: pos.description || '',
      confidence: 'manual', reasoning: 'Manual selection', matchPass: 'manual',
      lvQuantity: pos.quantity, lvUnit: pos.unit,
    } : null;
    setUpdating(true);
    try {
      const result = await api.updateReviewMatch(index, lvMatch);
      if (result) updateItemMatch(index, lvMatch, result.match_summary);
      else { useProjectStore.getState().setToast('Match update failed', 'error'); setMatchEditIndex(null); }
    } catch (e) {
      useProjectStore.getState().setToast(`Match update failed: ${e}`, 'error'); setMatchEditIndex(null);
    } finally { setUpdating(false); }
  };

  return (
    <div className={`match-dropdown${updating ? ' match-dropdown-busy' : ''}`} ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
      <input className="match-dropdown-search" type="text" placeholder={updating ? 'Updating...' : 'Search LV positions...'} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus disabled={updating} onClick={(e) => e.stopPropagation()} />
      <div className="match-dropdown-list">
        <div className={`match-dropdown-item match-dropdown-clear${updating ? ' disabled' : ''}`} onClick={() => !updating && handleSelect(null)}>Clear match</div>
        {filtered.map((pos, i) => (
          <div key={`${pos.position_number}-${i}`} className={`match-dropdown-item${updating ? ' disabled' : ''}`} onClick={() => !updating && handleSelect(pos)}>
            <span className="match-dd-id">{pos.position_number}</span>
            <span className="match-dd-desc">{pos.description}</span>
            {pos.quantity != null && <span className="match-dd-qty">{pos.quantity} {pos.unit}</span>}
          </div>
        ))}
        {filtered.length === 0 && <div className="match-dropdown-empty">No positions found</div>}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Publish Result Modal
   ═══════════════════════════════════════════════════════════════ */

function PublishResultModal({ result, onClose }: { result: PublishResult; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(result.location).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card publish-result-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Published</h2>
        <div className="publish-result-details">
          <div className="publish-result-row"><span className="dim">Location</span><code className="publish-result-path" title={result.location}>{result.location}</code></div>
          <div className="publish-result-row"><span className="dim">Artifacts</span><span>{result.artifacts} files</span></div>
          {result.boq_items != null && <div className="publish-result-row"><span className="dim">BoQ items</span><span>{result.boq_items}</span></div>}
          {result.plans != null && <div className="publish-result-row"><span className="dim">Plans</span><span>{result.plans}</span></div>}
        </div>
        <div className="modal-actions">
          <button className="btn-approve" onClick={handleCopy}>{copied ? 'Copied!' : 'Copy Path'}</button>
          <button className="btn-reject" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
